import { AuthWireService } from "@gsb-core/client/internal";
import { authOutcomeSchema, verifyOutcomeSchema, } from "../auth/auth-outcome-schema.js";
import { EntityService, BrowserRegistry, ProtocolGuards, } from "@gsb-core/client/internal";
import { GsbApiService } from "../../api/gsb-api.service.js";
import * as z from "zod/v4/mini";
import { coreEntityQuerySchema } from "../../client-validation.js";
// Store a singleton instance
let serviceInstance = null;
export class GsbEntityService extends EntityService {
    authWireService;
    resolveApiService() {
        return GsbApiService.getInstance();
    }
    constructor(useCache = true, useBulk = true, useAuth = true, apiBaseUrl) {
        super(useCache, useBulk, useAuth, apiBaseUrl);
        this.composeQueryValidation(coreEntityQuerySchema);
        this.authWireService = new AuthWireService(this.apiService, {
            useBulk,
            useAuth,
            apiBaseUrl,
        });
        this.authWireService.composeAuthValidation((value) => authOutcomeSchema.safeParse(value).success, (value) => verifyOutcomeSchema.safeParse(value).success);
    }
    // Static method to get the singleton instance
    static getInstance(useCache = true) {
        if (typeof window !== "undefined") {
            const instance = BrowserRegistry.acquire("advancedEntity", () => serviceInstance ?? new GsbEntityService(useCache));
            if (!GsbEntityService.hasAdvancedApi(instance))
                throw new Error("Incompatible GSB entity service registration.");
            return instance;
        }
        if (!serviceInstance) {
            serviceInstance = new GsbEntityService(useCache);
        }
        return serviceInstance;
    }
    static hasAdvancedApi(value) {
        if (!ProtocolGuards.record(value))
            return false;
        return [
            "query",
            "save",
            "delete",
            "getToken",
            "verifyToken",
            "refreshToken",
            "queryMapped",
            "getCode",
            "saveMappedItems",
            "removeMappedItems",
            "getDefinition",
            "addVersion",
            "restoreVersion",
            "runWorkflow",
            "startWorkflow",
            "runWfFunction",
            "testWfFunction",
            "iterateTask",
            "iterateOnce",
            "submitWorkflowTask",
            "changePassword",
            "uploadFile",
        ].every((method) => typeof value[method] === "function");
    }
    async getToken(request) {
        return this.authWireService.getToken(request);
    }
    async verifyToken(request, token) {
        return this.authWireService.verifyToken(request, token);
    }
    async refreshToken(request, token, tenantCode) {
        return this.authWireService.refreshToken(request, token, tenantCode);
    }
    async queryMapped(req, token, tenantCode) {
        if (!req.mapColName && !req.propertyName && !req.propName) {
            throw new Error("Property name is required");
        }
        this.reduceEntityDef(req);
        req.disableTransaction = true;
        const response = await this.callApi({
            method: "POST",
            protocol: "https",
            hostName: "api",
            content: req,
        }, "/api/entity/queryMapped", token, tenantCode);
        return response;
    }
    async getCode(req, token, tenantCode) {
        if (!req.codeGeneratorId) {
            throw new Error("Code generator id is required");
        }
        const response = await this.callApi({
            method: "POST",
            protocol: "https",
            hostName: "api",
            content: req,
        }, "/api/entity/getCode", token, tenantCode);
        return response;
    }
    async saveMappedItems(req, token, tenantCode) {
        if (!req.items) {
            throw new Error("Items are required");
        }
        if (!req.propName && !req.mapColName && !req.propertyName) {
            throw new Error("Property name is required");
        }
        this.reduceEntityDef(req);
        const response = await this.callApi({
            method: "POST",
            protocol: "https",
            hostName: "api",
            content: req,
        }, "/api/entity/saveMappedItems", token, tenantCode);
        return response;
    }
    async removeMappedItems(req, token, tenantCode) {
        if (!req.items) {
            throw new Error("Items are required");
        }
        if (!req.propName && !req.mapColName && !req.propertyName) {
            throw new Error("Property name or mapped column name is required");
        }
        this.reduceEntityDef(req);
        const response = await this.callApi({
            method: "POST",
            protocol: "https",
            hostName: "api",
            content: req,
        }, "/api/entity/removeMappedItems", token, tenantCode);
        return response;
    }
    async getDefinition(req, token, tenantCode) {
        this.reduceEntityDef(req);
        const response = await this.callApi({
            method: "POST",
            protocol: "https",
            hostName: "api",
            content: req,
        }, "/api/entityDef/get", token, tenantCode);
        const parsed = z
            .looseObject({
            status: z.literal(0),
            success: z.optional(z.literal(true)),
            entityDef: z.looseObject({
                id: z.string().check(z.minLength(1)),
                name: z.string().check(z.minLength(1)),
                properties: z.optional(z.array(z.looseObject({
                    id: z.optional(z.string().check(z.minLength(1))),
                    name: z.string().check(z.minLength(1)),
                }))),
            }),
        })
            .safeParse(response);
        if (!parsed.success) {
            throw new Error("Invalid entity definition response. Reload or contact the workspace administrator.");
        }
        if ((req.entityDef.id && parsed.data.entityDef.id !== req.entityDef.id) ||
            (req.entityDef.name && parsed.data.entityDef.name !== req.entityDef.name)) {
            throw new Error("Entity definition response returned another target.");
        }
        return response;
    }
    async addVersion(request, token, tenantCode) {
        return this.callApi({
            method: "POST",
            protocol: "https",
            hostName: "api",
            content: request,
            useBulk: false,
        }, "/api/entity/addVersion", token, tenantCode);
    }
    async restoreVersion(request, token, tenantCode) {
        return this.callApi({
            method: "POST",
            protocol: "https",
            hostName: "api",
            content: request,
            useBulk: false,
        }, "/api/entity/restoreVersion", token, tenantCode);
    }
    async runWorkflow(req, token, tenantCode) {
        return this.callApi({
            method: "POST",
            protocol: "https",
            hostName: "api",
            content: req,
            useBulk: false,
        }, "/api/workflow/runWorkflow", token, tenantCode);
    }
    async startWorkflow(req, token, tenantCode) {
        return this.callApi({
            method: "POST",
            protocol: "https",
            hostName: "api",
            content: req,
            useBulk: false,
        }, "/api/workflow/startWorkflow", token, tenantCode);
    }
    async runWfFunction(req, token, tenantCode) {
        return this.authWireService.runWfFunction(req, token, tenantCode);
    }
    async testWfFunction(req, token, tenantCode) {
        return this.callApi({
            method: "POST",
            protocol: "https",
            hostName: "api",
            content: req,
            useBulk: false,
        }, "/api/workflow/testFunction", token, tenantCode);
    }
    async iterateTask(req, token, tenantCode) {
        return this.callApi({
            method: "POST",
            protocol: "https",
            hostName: "api",
            content: req,
            useBulk: false,
        }, "/api/workflow/iterateTask", token, tenantCode);
    }
    async iterateOnce(req, token, tenantCode) {
        return this.callApi({
            method: "POST",
            protocol: "https",
            hostName: "api",
            content: req,
            useBulk: false,
        }, "/api/workflow/iterateOnce", token, tenantCode);
    }
    async submitWorkflowTask(req, token, tenantCode) {
        return this.callApi({
            method: "POST",
            protocol: "https",
            hostName: "api",
            content: req,
            useBulk: false,
        }, "/api/workflow/submitWorkflowTask", token, tenantCode);
    }
    async changePassword(req, token, tenantCode) {
        return this.authWireService.changePassword(req, token, tenantCode);
    }
    async uploadFile(req, token, tenantCode) {
        // Prepare content for FormData conversion
        const response = await this.apiService.callApi({
            method: "POST",
            protocol: "https",
            hostName: "api",
            content: req,
            isFormData: true,
            useBulk: false,
        }, "/api/file/upload", token, tenantCode);
        return response;
    }
}
//# sourceMappingURL=gsb-entity.service.js.map